export const displayChipTypes = [
    { value: 'default', label: 'Default' },
    { value: 'primary', label: 'Primary' },
    { value: 'secondary', label: 'Secondary' },
    { value: 'success', label: 'Success' },
    { value: 'warning', label: 'Warning' },
    { value: 'info', label: 'Info' },
    { value: 'error', label: 'Error' }
];

export const muiChipColors = new Set(displayChipTypes.map(({ value }) => value));

const legacyChipColorAliases = new Map([
    ['neutral', 'default']
]);

export const getChipColorIssue = (value) => {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    const rawValue = String(value).trim();
    const normalizedValue = rawValue.toLowerCase();

    if (muiChipColors.has(normalizedValue)) {
        return null;
    }

    if (legacyChipColorAliases.has(normalizedValue)) {
        return `uses legacy chip color "${rawValue}" and will render as "${legacyChipColorAliases.get(normalizedValue)}".`;
    }

    return `uses unsupported chip color "${rawValue}" and will render as "default".`;
};

export const normalizeChipType = (value) => {
    if (value === undefined || value === null || value === '') {
        return 'default';
    }

    const normalizedValue = String(value).trim().toLowerCase();
    if (muiChipColors.has(normalizedValue)) {
        return normalizedValue;
    }

    return legacyChipColorAliases.get(normalizedValue) || 'default';
};

export const safeChipColor = (value) => normalizeChipType(value);

export const safeSortOrder = (value, fallback = 999) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
};

export const isValidSortOrderInput = (value) => (
    value !== '' && value !== null && Number.isFinite(Number(value))
);

export const sortedDisplayChips = (chips = []) => [...chips].sort((left, right) => (
    safeSortOrder(left?.order, 0) - safeSortOrder(right?.order, 0)
));

export const normalizeDisplayChipEntry = (chip = {}, index = 0) => ({
    text: String(chip?.text || '').trim(),
    type: normalizeChipType(chip?.type || chip?.color),
    visible: chip?.visible !== false,
    order: safeSortOrder(chip?.order, index + 1)
});

export const normalizeDisplayChipList = (chips = []) => (
    (Array.isArray(chips) ? chips : []).map(normalizeDisplayChipEntry)
);

export const serializeDisplayChipList = (chips = []) => sortedDisplayChips(chips).map((chip) => ({
    text: String(chip?.text || '').trim(),
    type: normalizeChipType(chip?.type || chip?.color),
    visible: Boolean(chip?.visible),
    order: safeSortOrder(chip?.order, 0)
}));

export const visibleTopBadgeTexts = (chips = []) => serializeDisplayChipList(chips)
    .filter((chip) => chip.visible && chip.text)
    .map((chip) => chip.text);

export const firstVisibleTopBadgeColor = (chips = [], fallback = 'default') => {
    const firstVisible = serializeDisplayChipList(chips).find((chip) => chip.visible && chip.text);
    return firstVisible ? normalizeChipType(firstVisible.type) : safeChipColor(fallback);
};

export const getChipListErrors = (chips = [], label = 'chips') => {
    const errors = [];

    (Array.isArray(chips) ? chips : []).forEach((chip, index) => {
        if (chip?.visible && !String(chip?.text || '').trim()) {
            errors.push(`${label} ${index + 1} text is required when shown.`);
        }
        if (!Number.isFinite(Number(chip?.order))) {
            errors.push(`${label} ${index + 1} order must be numeric.`);
        }
        if (!muiChipColors.has(normalizeChipType(chip?.type || chip?.color))) {
            errors.push(`${label} ${index + 1} color must be MUI-safe.`);
        }
    });

    return errors;
};
